import classNames from 'classnames';
import { Link } from 'core/i18n/navigation';
import { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown } from '@fortawesome/pro-solid-svg-icons';
import { appRoutes } from 'config';

export type BannerVariant = 'pro' | 'madeIn';

const LINK_CLASS =
  'font-bold hover:underline hover:underline-offset-2 hover:opacity-90';

const BANNERS: Record<
  BannerVariant,
  { icon: ReactNode; iconClassName?: string }
> = {
  pro: {
    icon: (
      <FontAwesomeIcon icon={faCrown} className="h-3.5 w-3.5 text-[#ffa42e]" />
    ),
    iconClassName: 'text-mf-yellow',
  },
  madeIn: { icon: '🌊' },
};

// Message uses <Trans>: <sm> hides the clause < sm (keeps the bar one line),
// <cta>/<link> are the inline links. "Manly" is the gag (our suburb
// Manly Vale styled like Silicon Valley), so it stays untranslated.
const BannerMessage = ({ variant }: { variant: BannerVariant }) => {
  const t = useTranslations();
  if (variant === 'pro') {
    return t.rich('pages.home.banner.proMessage', {
      sm: (chunks) => <span className="hidden sm:inline">{chunks}</span>,
      cta: (chunks) => (
        <Link
          href={appRoutes.PRICING.INDIVIDUAL}
          className={classNames('ml-[6px] whitespace-nowrap', LINK_CLASS)}
        >
          {chunks}
        </Link>
      ),
    });
  }
  return t.rich('pages.home.banner.madeInMessage', {
    link: (chunks) => (
      <a
        href="https://en.wikipedia.org/wiki/Manly_Vale"
        target="_blank"
        rel="noopener noreferrer"
        className={LINK_CLASS}
      >
        {chunks}
      </a>
    ),
    sm: (chunks) => <span className="hidden sm:inline">{chunks}</span>,
  });
};

interface Props {
  variant?: BannerVariant;
  onDismiss?: () => void;
}

export const Banner = ({ variant = 'pro', onDismiss }: Props) => {
  const t = useTranslations();
  const config = BANNERS[variant];

  return (
    <div className={styles.bar}>
      <span className={classNames(styles.icon, config.iconClassName)}>
        {config.icon}
      </span>
      <span>
        <BannerMessage variant={variant} />
      </span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('pages.home.banner.dismiss')}
          className={styles.dismiss}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            className="h-3.5 w-3.5"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      )}
    </div>
  );
};

const styles = {
  // `fixed` already anchors the absolutely-positioned dismiss button; px-10
  // reserves room so the centred message never slides under it.
  bar: 'fixed inset-x-0 top-0 z-30 flex h-9 items-center justify-center bg-[linear-gradient(90deg,#3d2bd1,#7b2fc9_55%,#c0379a)] px-10 text-center text-[13.5px] font-medium text-white',
  icon: 'mr-[7px]',
  dismiss:
    'absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-white/70 transition-colors hover:bg-white/10 hover:text-white',
};
